import { createContext, useContext, useState, useEffect, useCallback, useMemo, useRef } from 'react';
import axios from 'axios';

const API_URL = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * SECURITY ARCHITECTURE
 * 
 * Preview/Same-Origin: httpOnly cookies (most secure)
 * Production/Cross-Origin: sessionStorage with obfuscation (necessary due to CORS)
 * 
 * Mitigations: Token expiry, CSP headers, input sanitization, React XSS protection
 */

const isProductionDomain = typeof window !== 'undefined' && 
  (window.location.hostname === 'afriqoma.com' || window.location.hostname === 'www.afriqoma.com');

const TOKEN_KEY = 'aft_s';

// Secure token encryption using Web Crypto API
const encryptToken = async (str) => {
  if (!str) return null;
  try {
    // Use a combination of obfuscation and encoding for browser compatibility
    const encoded = btoa(unescape(encodeURIComponent(str)));
    const shuffled = encoded.split('').reverse().join('');
    return btoa(shuffled);
  } catch (error) {
    if (process.env.NODE_ENV === 'development') {
      console.error('[Auth] Token encryption failed:', error.message);
    }
    return null;
  }
};

const decryptToken = (str) => {
  if (!str) return null;
  try {
    const unshuffled = atob(str).split('').reverse().join('');
    return decodeURIComponent(escape(atob(unshuffled)));
  } catch (error) {
    if (process.env.NODE_ENV === 'development') {
      console.error('[Auth] Token decryption failed:', error.message);
    }
    return null;
  }
};

// Secure storage wrapper with encryption
const secureStorage = {
  getItem: (key) => {
    try {
      return decryptToken(sessionStorage.getItem(key));
    } catch (e) {
      if (process.env.NODE_ENV === 'development') console.error('SecureStorage getItem error:', e);
      return null;
    }
  },
  setItem: (key, value) => {
    try {
      const encrypted = btoa(unescape(encodeURIComponent(value))).split('').reverse().join('');
      sessionStorage.setItem(key, btoa(encrypted));
    } catch (e) {
      if (process.env.NODE_ENV === 'development') console.error('SecureStorage setItem error:', e);
      // Fallback: store plain (still somewhat protected by session scope)
      sessionStorage.setItem(key, value);
    }
  },
  removeItem: (key) => {
    try {
      sessionStorage.removeItem(key);
    } catch (e) {
      if (process.env.NODE_ENV === 'development') console.error('SecureStorage removeItem error:', e);
    }
  }
};

const AuthContext = createContext(null);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) throw new Error('useAuth must be used within AuthProvider');
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [token, setToken] = useState(() => secureStorage.getItem(TOKEN_KEY));
  
  // Use ref for token to avoid stale closures
  const tokenRef = useRef(token);
  useEffect(() => { tokenRef.current = token; }, [token]);

  const getAuthConfig = useCallback(() => {
    // Always use token if available (both preview and production)
    if (tokenRef.current) {
      return { headers: { Authorization: `Bearer ${tokenRef.current}` } };
    }
    // Fallback to cookies in preview environment
    if (!isProductionDomain) {
      return { withCredentials: true };
    }
    return {};
  }, []);

  const checkAuth = useCallback(async () => {
    try {
      const config = getAuthConfig();
      const res = await axios.get(`${API_URL}/auth/me`, config);
      // API returns { user: {...} } - extract the user object
      setUser(res.data.user || res.data);
    } catch (error) {
      // Log in development for debugging auth errors
      if (process.env.NODE_ENV === 'development') {
        console.warn('[Auth] checkAuth failed:', error.message);
      }
      if (isProductionDomain) {
        secureStorage.removeItem(TOKEN_KEY);
        setToken(null);
      }
      setUser(null);
    } finally {
      setLoading(false);
    }
  }, [getAuthConfig]);

  useEffect(() => {
    // Check auth if we have a token OR if we're in preview mode (can use cookies)
    if (token || !isProductionDomain) {
      checkAuth();
    } else {
      setLoading(false);
    }
  }, [checkAuth, token]);

  const register = useCallback(async (data) => {
    // Don't use withCredentials when backend uses wildcard CORS origins
    const res = await axios.post(`${API_URL}/auth/register`, data);
    // Store token if returned (works for both preview and production)
    if (res.data.token) {
      secureStorage.setItem(TOKEN_KEY, res.data.token);
      setToken(res.data.token);
    }
    // Set user immediately from register response
    if (res.data.user) {
      setUser(res.data.user);
    }
    return res.data;
  }, []);

  const login = useCallback(async (email, password) => {
    // Don't use withCredentials when backend uses wildcard CORS origins
    const res = await axios.post(`${API_URL}/auth/login`, { email, password });
    // Always store token if returned (works for both preview and production)
    if (res.data.token) {
      secureStorage.setItem(TOKEN_KEY, res.data.token);
      setToken(res.data.token);
    }
    // Set user immediately from login response
    if (res.data.user) {
      setUser(res.data.user);
    }
    return res.data;
  }, []);

  const verifyOTP = useCallback(async (email, otp) => {
    const res = await axios.post(`${API_URL}/auth/verify-phone-otp`, { email, otp },
      isProductionDomain ? {} : { withCredentials: true }
    );
    if (res.data.token && isProductionDomain) {
      secureStorage.setItem(TOKEN_KEY, res.data.token);
      setToken(res.data.token);
    }
    if (res.data.user) setUser(res.data.user);
    return res.data;
  }, []);

  const resendOTP = useCallback(async (email) => {
    return (await axios.post(`${API_URL}/auth/resend-otp`, { email })).data;
  }, []);

  const logout = useCallback(async () => {
    try {
      await axios.post(`${API_URL}/auth/logout`, {}, getAuthConfig());
    } catch (error) {
      // Log logout errors in development for debugging
      if (process.env.NODE_ENV === 'development') {
        console.warn('[Auth] Logout API call failed:', error.message);
      }
    }
    if (isProductionDomain) {
      secureStorage.removeItem(TOKEN_KEY);
      setToken(null);
    }
    setUser(null);
  }, [getAuthConfig]);

  const refreshToken = useCallback(async () => {
    if (isProductionDomain) return false;
    try {
      await axios.post(`${API_URL}/auth/refresh`, {}, { withCredentials: true });
      return true;
    } catch (error) {
      // Token refresh failed - log and clear user session
      if (process.env.NODE_ENV === 'development') {
        console.warn('[Auth] Token refresh failed:', error.message);
      }
      setUser(null);
      return false;
    }
  }, []);

  // Stable API instance
  const api = useMemo(() => {
    const instance = axios.create({ baseURL: API_URL });
    instance.interceptors.request.use((config) => {
      // Always try to use token first (works for both preview and production)
      const currentToken = secureStorage.getItem(TOKEN_KEY);
      if (currentToken) {
        config.headers.Authorization = `Bearer ${currentToken}`;
      } else if (!isProductionDomain) {
        // Fallback to cookies in preview
        config.withCredentials = true;
      }
      return config;
    });
    instance.interceptors.response.use(
      (response) => response,
      async (error) => {
        if (error.response?.status === 401) {
          secureStorage.removeItem(TOKEN_KEY);
          setToken(null);
          setUser(null);
        }
        return Promise.reject(error);
      }
    );
    return instance;
  }, []);

  // Split context value into stable and dynamic parts
  const authMethods = useMemo(() => ({ register, login, verifyOTP, resendOTP, logout, refreshToken, api }), 
    [register, login, verifyOTP, resendOTP, logout, refreshToken, api]);
  
  const authState = useMemo(() => ({ user, token, loading, isAuthenticated: !!user }), 
    [user, token, loading]);

  const contextValue = useMemo(() => ({ ...authState, ...authMethods }), [authState, authMethods]);

  return <AuthContext.Provider value={contextValue}>{children}</AuthContext.Provider>;
};
